import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_URL } from '../config';
import './LiveActivityToast.css';

// Anonymized creator names for social proof
const CREATOR_NAMES = [
  'Alex', 'Sam', 'Jordan', 'Taylor', 'Morgan', 'Casey', 'Riley', 'Quinn',
  'Avery', 'Blake', 'Drew', 'Hayden', 'Jamie', 'Logan', 'Reese', 'Parker',
  'Skyler', 'Sydney', 'Finley', 'Peyton', 'Rowan', 'Emery', 'Kendall', 'Devon',
];

function randomName() {
  return CREATOR_NAMES[Math.floor(Math.random() * CREATOR_NAMES.length)];
}

function timeAgo(isoString) {
  if (!isoString) return 'just now';
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function truncatePrompt(prompt, maxLen = 36) {
  if (!prompt) return 'a new design';
  const clean = prompt.trim();
  return clean.length > maxLen ? clean.slice(0, maxLen - 1) + '…' : clean;
}

let seenIds = new Set();

export default function LiveActivityToast() {
  const [toasts, setToasts] = useState([]);
  const queueRef = useRef([]);
  const timerRef = useRef(null);
  const fetchedOnce = useRef(false);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t));
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 400);
  }, []);

  // Drain queue: show one toast at a time with spacing
  const drainQueue = useCallback(() => {
    if (timerRef.current) return;
    const show = () => {
      if (queueRef.current.length === 0) {
        timerRef.current = null;
        return;
      }
      const toast = queueRef.current.shift();
      setToasts(prev => {
        // Max 3 visible at once
        const trimmed = prev.length >= 3 ? prev.slice(1) : prev;
        return [...trimmed, toast];
      });
      // Auto-dismiss after 5s
      setTimeout(() => removeToast(toast.id), 5000);
      // Next toast after 4s gap
      timerRef.current = setTimeout(show, 4000);
    };
    timerRef.current = setTimeout(show, 800);
  }, [removeToast]);

  const fetchAndQueue = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/designs?limit=10&sort=recent`);
      if (!res.ok) return;
      const data = await res.json();
      const designs = (data.designs || []).filter(d =>
        d.image_url &&
        !d.image_url.includes('picsum') &&
        d.prompt &&
        !seenIds.has(d.id)
      );

      if (designs.length === 0) return;

      // On first fetch, only queue 1-2 designs to avoid overwhelming
      const limit = fetchedOnce.current ? designs.length : Math.min(2, designs.length);
      fetchedOnce.current = true;

      const newToasts = designs.slice(0, limit).map(d => {
        seenIds.add(d.id);
        return {
          id: `${d.id}-${Date.now()}`,
          imageUrl: d.image_url,
          prompt: truncatePrompt(d.prompt),
          creator: randomName(),
          timeAgo: timeAgo(d.created_at),
          exiting: false,
        };
      });

      // Trim seenIds to avoid unbounded growth
      if (seenIds.size > 200) {
        const arr = [...seenIds];
        seenIds = new Set(arr.slice(-100));
      }

      queueRef.current.push(...newToasts);
      drainQueue();
    } catch {
      // non-critical
    }
  }, [drainQueue]);

  useEffect(() => {
    // Initial fetch after 3s delay (let page settle)
    const initTimer = setTimeout(fetchAndQueue, 3000);
    // Refresh every 45s for new designs
    const interval = setInterval(fetchAndQueue, 45000);
    return () => {
      clearTimeout(initTimer);
      clearInterval(interval);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [fetchAndQueue]);

  if (toasts.length === 0) return null;

  return (
    <div className="live-activity-container" aria-live="polite" aria-label="Live activity">
      {toasts.map(toast => (
        <div
          key={toast.id}
          className={`live-toast ${toast.exiting ? 'live-toast--exit' : 'live-toast--enter'}`}
        >
          <div className="live-toast-img-wrap">
            <img src={toast.imageUrl} alt="" className="live-toast-img" loading="lazy" />
            <span className="live-toast-pulse" />
          </div>
          <div className="live-toast-body">
            <p className="live-toast-text">
              <strong>{toast.creator}</strong> just created{' '}
              <em>"{toast.prompt}"</em>
            </p>
            <span className="live-toast-time">{toast.timeAgo}</span>
          </div>
          <button
            className="live-toast-close"
            onClick={() => removeToast(toast.id)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
